import { html } from "lit";
import { msg } from "@omegagrid/localize";
import { MenuItem } from "@omegagrid/core";
import { FormContainer } from "@omegagrid/form";
import { NodeModel, NodeSource, TreeSource } from "@omegagrid/tree";
import rp, { BrowserAdapter, BrowserOptions, formatDateTime, helpers } from "@reporting/rp-app-host";
import { TableDefinition} from "@reporting/rp-types/mdm";
import { AccountingRights } from '@reporting/rp-types/core';
import { EntityAccessType } from '@reporting/rp-types/users';
import { TableType, TableTypes } from "../types";
import { Sidebar } from "../components";
import { TableDefFormAdapter } from '../adapters';

type ExportFormat = 'xlsx' | 'csv';

interface TreeNode {
	key: string;
	name: string;
	table?: TableDefinition;
	children: Map<string, TreeNode>;
}

export class MdmBrowserAdapter extends BrowserAdapter {

	getTooltip(node: NodeModel) {
		if (!node.properties?.table) return null;
		const table = node.properties.table as TableDefinition;
		return html`
			<div>
				ID: ${table.id_md}<br>
				${msg('Type')}: ${table.type}<br>
				${msg('Sync')}: ${msg(table.sync ? 'Yes' : 'No')}<br>
				${msg('Edited')}: ${formatDateTime(table.date_created)}
			</div>
		`;
	}

	async getTree(): Promise<TreeSource> {
		const tables = await rp.ms('mdm').get<TableDefinition[]>(`tableDefinitions`);

		// Build a nested tree of folders and tables so we can flatten it pre-order
		// with folders sorted above tables on every level (not just at the root).
		const root: TreeNode = { key: '', name: '', children: new Map() };

		tables.forEach(table => {
			const nameParts = table.md_name.split('/');
			const tableName = nameParts.pop();
			let parent = root;
			nameParts.forEach((folderName, index) => {
				const path = nameParts.slice(0, index + 1).join('/');
				let folder = parent.children.get(path);
				if (!folder) {
					folder = { key: path, name: folderName, children: new Map() };
					parent.children.set(path, folder);
				}
				parent = folder;
			});
			const key = `mdm-values-${table.type}-${table.id_md}`;
			parent.children.set(key, { key, name: tableName, table, children: new Map() });
		});

		const items: NodeSource[] = [];
		const walk = (node: TreeNode, level: number) => {
			// Stable sort: folders first, tables after; insertion (API) order kept within each group.
			const children = [...node.children.values()]
				.sort((a, b) => (a.table ? 1 : 0) - (b.table ? 1 : 0));
			children.forEach(child => {
				if (child.table) {
					items.push({
						k: child.key,
						v: child.name,
						l: level,
						icon: {
							icon: 'table',
							statusIcon: child.table.type as TableType == TableTypes.SQL ? 'database' : null,
							statusColor: 'yellow'
						},
						props: { table: child.table }
					});
				} else {
					items.push({
						k: child.key,
						v: child.name,
						l: level,
						collapsed: true,
						folder: BrowserAdapter.folderOptions
					});
					walk(child, level + 1);
				}
			});
		};
		walk(root, 0);
		return items;
	}

	async getMenuItems(node: NodeModel): Promise<MenuItem[]> {
		const items: MenuItem[] = [];
		if (node.folder && rp.userProvider.hasRight(AccountingRights.MDMDefine)) { // folder actions
			items.push({ key: 'newMdmTable', value: `${msg('New table')} - MDM`, icon: 'table' });
			items.push({ key: 'newSqlTable', value: `${msg('New table')} - SQL`, icon: 'database' });
		} else if (!node.folder) { // table actions
			if (rp.userProvider.hasRight(AccountingRights.MDMDefine)) {
				items.push({ key: 'definition', value: msg('Definition'), icon: 'gear' });
				items.push({ key: 'properties', value: msg('Properties'), icon: 'pen-to-square' });
				items.push({ key: 'access', value: msg('Access'), icon: 'shield-check' });
				items.push({ key: 'delete', value: msg('Delete'), icon: { icon: 'trash', color: 'red' } });
			}
			items.push({ key: 'copyPath', value: `${msg('Copy path')}`, icon: 'copy' });
			items.push({ key: 'export-excel', value: `${msg('Export')} - Excel`, icon: 'file-excel' });
			items.push({ key: 'export-csv', value: `${msg('Export')} - CSV`, icon: 'file-csv' });
			if (rp.userProvider.hasRight(AccountingRights.Admin))
				items.push({ key: 'embedding', value: msg('Embedding'), icon: 'code' });
		}
		return items;
	}

	getOptions(): Partial<BrowserOptions> {
		return this.createOptions({
			selectMode: 'none',
			expanders: false,
			search: false,
			contextMenuButton: true,
		});
	}

	async onAction(key: string, node: NodeModel) {
		if (node.folder) { // folder actions
			let component: FormContainer;
			switch (key) {
				case 'newMdmTable':
					component = await rp.host.createComponent(`mdm-tableDefForm-MDM`) as unknown as FormContainer;
					(component.adapter as TableDefFormAdapter).path = node.key + '/';
					rp.host.openModalSidebar({ component: component, caption: `${msg('New table')} - MDM` });
					break;
				case 'newSqlTable':
					component = await rp.host.createComponent(`mdm-tableDefForm-SQL`) as unknown as FormContainer;
					(component.adapter as TableDefFormAdapter).path = node.key + '/';
					rp.host.openModalSidebar({ component: component, caption: `${msg('New table')} - SQL` });
					break;
			}
		} else { // talbe actions
			const table = node.properties?.table as TableDefinition;
			switch (key) {
				case 'definition':
					rp.host.open(`mdm-columnsDef-${table.id_md}`, { title: `${msg('Definition')} - ${node.value}` });
					break;
				case 'properties':
					rp.host.openModalSidebar({ id: `mdm-tableDefForm-${table.type}-${table.id_md}`, caption: `${msg('Properties')} - ${node.value}` });
					break;
				case 'access':
					rp.host.open(`users-entityUsers-${EntityAccessType.MdTable}-${table.id_md}`, { title: `${msg('Access')}: ${node.value}` });
					break;
				case 'delete':
					if (table) {
						const command = await rp.host.openMenu({
							content: 'yes_no',
							text: `${msg('Are you sure you want to delete')} '#${table.id_md} ${table.md_name}'?`
						});
						if (command?.id == 'Y') {
							await rp.ms('mdm').delete(`tableDefinitions/${table.id_md}`);
							(rp.host.components.get('mdm') as Sidebar)?.refresh();
						}
					}
					break;
				case 'copyPath':
					await navigator.clipboard.writeText(table.md_name);
					rp.host.showAlert({ type: 'info', title: msg('Copy path'), content: msg('Copied to clipboard: {v}', { data: { v: table.md_name } }) });
					break;
				case 'export-excel':
					await this.export(table, 'xlsx');
					break;
				case 'export-csv':
					await this.export(table, 'csv');
					break;
				case 'embedding':
					rp.host.openModalSidebar({ id: `accounting-embedForm-MDM-${table.id_md}`, caption: `${msg('Embedding')} - ${node.value}`, width: 280 });
					break;
			}
		}
	}

	async export(table: TableDefinition, format: ExportFormat = 'csv') {
		rp.host.showAlert({ type: 'info', title: msg('Export'), content: msg('Export started.') });
		await rp.ms('mdm').download(`export/${table.id_md}`, {
			fileName: helpers.createExportFilename(format),
			csvParams: { header: true, delimiter: ',', encoding: 'utf-8' },
			format,
			count: 999999
		});
	}

}
